import { FC,  useEffect, useState } from 'react';
import { useTheme } from 'styled-components';

import { EventTrackLabel } from '../../constants/ga4';
import { useCartContext } from '../../hooks/contexts/cart.context';
import { DataCarouselProps, useEventContext } from '../../hooks/contexts/event.context';
import { useI18nContext } from '../../hooks/contexts/i18n.context';
import { ListView, useUiContext } from '../../hooks/contexts/ui.context';
import useFormattedDateTimeZone from '../../hooks/useFormattedDateTimeZone';
import useRules from '../../hooks/useRules';
import GoogleAnalytics from '../../trackings/GoogleAnalytics';
import { TrackingItem } from '../../types/ga4';
import { Ticket } from '../../types/ticket';
import ForwardIcon from '../SvgCustom/svg/ForwardIcon';
import * as Styles from './ticket-list-groups.styles';

const TicketListGroups: FC = () => {
  const theme = useTheme();
  const [itemCarousel, setItemCarousel] = useState<DataCarouselProps | null>();
  const [ticketsFilteredBySession, setTicketsFilteredBySession] = useState<Ticket[]>([]);
  const {
    tickets,
    selectedIdDateCarousel,
    isFootball,
    dataCarousel,
    couponApplied,
    passKeyApplied,
    selectTicketGroup,
    event
  } = useEventContext();

  const {  handleFormatedDate } = useFormattedDateTimeZone();
  const { currentLocale : { locale } } = useI18nContext();
  const { executed, setExecuted, handleChangeListView } = useUiContext();
  const { ticketGroupStatus } = useRules();
  const { getTotalTicketsByGroup } = useCartContext();

  const defaultImageColorState = {} as Record<string, string>;

  const soldOff = (ticket: Ticket) => (
    ticket.type.reduce((acc, type) => {
      return acc + type.quantityInStock;
    }, 0) === 0
  );
  const totalTickets = (groupId: number) => getTotalTicketsByGroup({ groupId });

  tickets.forEach((ticket) => {
    defaultImageColorState[ticket.id] = soldOff(ticket)
      ? theme.colors.button.text.disabled
      : theme.colors.text.secondary;
  });

  const [imageColor, setImageColor] = useState(defaultImageColorState);

  const handleOpenedAcordeon = (ticket: Ticket) => {
    if (!executed && (couponApplied?.isValid || passKeyApplied)) {
      return ticket.type.some((type) => type.tagType) || ticket.type.some((type) => type.hidden);
    }
  };

  const handleTracking = (curItem: DataCarouselProps) => {
    const trackingTickets = tickets.filter(({ id }) => isFootball || curItem?.groupsReference.includes(id));

    let trackingData = [] as TrackingItem[];
    trackingTickets.forEach((ticketGroup) => {
      trackingData = ticketGroup.type.map((curTicket) => ({
        currency : String(event?.accepted_currencies),
        item_id : String(event?.id),
        item_name : String(event?.title),
        item_category : ticketGroup.isPassport ? 'Passaporte' : (
          handleFormatedDate(curTicket.dates[0].datetime, locale, event?.timezone ?? 'America/Sao_Paulo')
        ),
        item_brand : ticketGroup.name,
        item_variant : curTicket.name,
        coupon : couponApplied ? couponApplied.coupon.name : '',
        discount : couponApplied ? couponApplied.coupon.value_discount : 0,
        price : curTicket.price - (couponApplied ? couponApplied.coupon.value_discount : 0),
      }));
    });

    GoogleAnalytics.trackViewItem(EventTrackLabel.VIEW_ITEM, trackingData);
  };

  useEffect(() => {
    const itemCarousel = dataCarousel.find((item) => item.id === selectedIdDateCarousel)!;
    setItemCarousel(itemCarousel);
    if (itemCarousel) {
      handleTracking(itemCarousel);
    }
  }, [dataCarousel, selectedIdDateCarousel]);

  useEffect(() => {
    if (itemCarousel) {
      const filteredTickets = tickets.filter(ticket => itemCarousel.groupsReference.includes(ticket.id));
      setTicketsFilteredBySession(filteredTickets);
    }
  }, [tickets, itemCarousel]);

  useEffect(() => {
    ticketsFilteredBySession.forEach((ticket) => {
      if (handleOpenedAcordeon(ticket)) {
        selectTicketGroup(ticket);
        setExecuted(true);
        handleChangeListView(ListView.TICKETS);
      }
    });
  }, [ticketsFilteredBySession]);

  if (!itemCarousel) return null;

  const handleClick = (ticket: Ticket) => {
    selectTicketGroup(ticket);
    handleChangeListView(ListView.TICKETS);
  };

  return (
    <Styles.Container id="ticket-list-groups">
      {ticketsFilteredBySession
        .map((ticket) => (
          <Styles.Card
            key={ticket.id}
            $isSoldOff={soldOff(ticket)}
            onClick={() => !soldOff(ticket) && handleClick(ticket)}
            onMouseEnter={() => !soldOff(ticket) && setImageColor((state) => ({
              ...state, [ticket.id] : theme.colors.accent.primary
            }))}
            onMouseLeave={() => !soldOff(ticket) && setImageColor((state) => ({
              ...state, [ticket.id] : theme.colors.text.secondary
            }))}
          >
            <div className="top-side-card">
              <span>{ticket.name}</span>
              <button
                disabled={soldOff(ticket)}
              >
                <img src={ForwardIcon({ colorTheme : imageColor[ticket.id] })} alt="forward arrow" />
              </button>
            </div>
            {soldOff(ticket) && (
              <small>
                {ticketGroupStatus({ ticket })}
              </small>
            )}
            {totalTickets(ticket.id) > 0 && (
              <small>
                {`${totalTickets(ticket.id)} ingresso selecionado`}
              </small>
            )}
          </Styles.Card>
        ))
      }
    </Styles.Container>
  );
};

export default TicketListGroups;
